import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { HubPageGrid } from "@/components/content/HubPageGrid";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Badge } from "@/components/ui/badge";
import { NavLink } from "@/components/NavLink";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const comparisonPages = [
  {
    title: "Wix vs WordPress for Lawn Care",
    description: "Which platform is better for your lawn care business? A detailed comparison of features, SEO, and cost.",
    href: "/comparisons/wix-vs-wordpress-lawn-care/"
  },
  {
    title: "Squarespace vs WordPress for Landscaping",
    description: "Compare Squarespace and WordPress for your landscaping business website needs.",
    href: "/comparisons/squarespace-vs-wordpress-landscaping/"
  },
  {
    title: "Best Website Builder for Lawn Care",
    description: "Find the best website builder for lawn care companies based on ease, cost, and results.",
    href: "/comparisons/best-website-builder-lawn-care/"
  },
  {
    title: "Best Website Builder for Landscapers",
    description: "Discover which website builder works best for landscaping companies and why.",
    href: "/comparisons/best-website-builder-landscapers/"
  },
  {
    title: "WordPress vs Website Builder for Lawn Care",
    description: "Should you use WordPress or a drag-and-drop builder? Here's what works for lawn care.",
    href: "/comparisons/wordpress-vs-website-builder-lawn-care/"
  },
  {
    title: "Agency vs Freelancer for Lawn Care Websites",
    description: "Hire an agency or freelancer? Compare cost, quality, and support for your lawn care site.",
    href: "/comparisons/agency-vs-freelancer/"
  },
  {
    title: "One-Time vs Subscription Website for Lawn Care",
    description: "Is a one-time website purchase or subscription model better for lawn care businesses?",
    href: "/comparisons/one-time-vs-subscription-website/"
  },
  {
    title: "Cheap vs Professional Lawn Care Website",
    description: "What's the real difference between cheap and professional lawn care websites?",
    href: "/comparisons/cheap-vs-professional-website/"
  },
  {
    title: "SEO Website vs Ads-First for Lawn Care",
    description: "Should you invest in SEO or Google Ads first? Compare strategies for lawn care leads.",
    href: "/comparisons/seo-vs-google-ads/"
  },
  {
    title: "Website Only vs Website + GBP Optimization",
    description: "Is your website enough, or do you need Google Business Profile optimization too?",
    href: "/comparisons/website-vs-website-plus-gbp/"
  },
  {
    title: "Best CRM & Forms for Lawn Care Websites",
    description: "Compare the best forms and CRM integrations for capturing lawn care leads.",
    href: "/comparisons/best-crm-forms-lawn-care/"
  },
  {
    title: "Call Tracking Tools for Lawn Care Websites",
    description: "Track your phone leads effectively. Compare call tracking tools for lawn care sites.",
    href: "/comparisons/call-tracking-tools/"
  }
];

const faqs = [
  {
    question: "Which website builder is best for lawn care businesses?",
    answer: "For most lawn care businesses, WordPress offers the best balance of SEO power, flexibility, and long-term scalability. However, if you want simplicity without technical hassle, a done-for-you website subscription is often the most cost-effective choice."
  },
  {
    question: "Is Wix good enough for a lawn care website?",
    answer: "Wix can work for basic websites, but it has SEO limitations that may hurt your rankings compared to WordPress or professionally built sites. If you're serious about getting organic leads, consider alternatives."
  },
  {
    question: "Should I hire an agency or freelancer for my lawn care website?",
    answer: "Agencies typically offer more comprehensive support and ongoing maintenance, while freelancers may cost less upfront. Consider a website subscription model for the best of both: professional quality with ongoing support."
  },
  {
    question: "What's better: SEO or Google Ads for lawn care leads?",
    answer: "Both work, but they serve different purposes. SEO builds long-term organic traffic, while Google Ads delivers immediate leads. Most successful lawn care businesses use both strategies together."
  },
  {
    question: "How much should I pay for a lawn care website?",
    answer: "Professional lawn care websites typically cost $2,000-$5,000 upfront or $200-$500/month on subscription. Avoid cheap $500 websites—they usually hurt your business more than they help."
  },
  {
    question: "Do I need Google Business Profile optimization with my website?",
    answer: "Yes! GBP optimization is critical for local lawn care businesses. It helps you appear in map results and attracts leads who are ready to hire. We recommend combining website and GBP optimization."
  },
  {
    question: "What's the difference between cheap and professional websites?",
    answer: "Professional websites are built for conversion: fast loading, mobile-optimized, SEO-structured, and designed to turn visitors into leads. Cheap websites often look outdated, load slowly, and don't rank well."
  },
  {
    question: "Should I get a one-time website or subscription?",
    answer: "Subscriptions offer ongoing updates, security, and support without large upfront costs. For busy lawn care business owners, subscription models are often the better choice for peace of mind."
  },
  {
    question: "What call tracking tools work best for lawn care websites?",
    answer: "CallRail and CallTrackingMetrics are popular choices. They integrate with Google Ads and your website to track which marketing channels drive phone calls."
  },
  {
    question: "How do these comparisons help my lawn care business?",
    answer: "These comparisons help you make informed decisions about your website and marketing investments. Making the right choice upfront saves money and helps you get leads faster."
  }
];

const ComparisonsHub = () => {
  const pageUrl = "https://www.leafrushmarketing.com/comparisons/";
  
  return (
    <>
      <SEO
        title="Lawn Care Website Comparisons | Leaf Rush Marketing"
        description="Compare website builders, agencies vs freelancers, SEO vs ads, and more. Make informed decisions for your lawn care or landscaping website."
        canonical={pageUrl}
        ogUrl={pageUrl}
        keywords="lawn care website builder comparison, best website for lawn care, wordpress vs wix lawn care"
      />
      <PageSchema
        pageUrl={pageUrl}
        pageName="Lawn Care Website Builder Comparison"
        pageDescription="Compare website builders, agencies, pricing models, and marketing strategies for lawn care and landscaping businesses."
        faqs={faqs}
      />

      <TopCTA />

      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge variant="secondary">Comparison Guides</Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Lawn Care Website Builder{" "}
              <span className="text-primary">Comparison Guides</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Make confident decisions about your lawn care or landscaping website. 
              Compare platforms, pricing models, and marketing strategies side-by-side.
            </p>
            <LastUpdated />
          </div>
        </div>
      </section>

      {/* Start Here Section */}
      <section className="py-12 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-center">Start Here</h2>
            <p className="text-center text-muted-foreground mb-8">
              Already know you need a professional website? Check out our done-for-you options:
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <NavLink to="/websites/">
                <Button size="lg" className="gap-2">
                  Website Pricing
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </NavLink>
              <NavLink to="/lawn-care-website-design/">
                <Button size="lg" variant="outline">
                  Lawn Care Websites
                </Button>
              </NavLink>
              <NavLink to="/landscaping-website-design/">
                <Button size="lg" variant="outline">
                  Landscaping Websites
                </Button>
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      {/* Grid of Comparison Pages */}
      <section className="py-16">
        <div className="container px-4 md:px-6">
          <h2 className="text-3xl font-bold mb-8 text-center">All Comparison Guides</h2>
          <HubPageGrid items={comparisonPages} />
        </div>
      </section>

      <MidPageCTA />

      {/* Internal Links */}
      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <InternalLinks
              title="More Resources"
              links={[
                { to: "/website-fixes/", label: "Website Problems & Fixes" },
                { to: "/website-templates/", label: "Templates & Checklists" },
                { to: "/service-pages-that-rank/", label: "Service Pages That Rank" },
                { to: "/service-area-pages-local-seo/", label: "Service Area Pages for Local SEO" },
                { to: "/growth-add-ons/google-ads/", label: "Google Ads Management" },
                { to: "/growth-add-ons/gbp/", label: "Google Business Profile Optimization" },
                { to: "/resources/", label: "All Resources" },
                { to: "/contact/", label: "Contact Us" }
              ]}
            />
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />

      <BottomCTA />

      <CalendlyEmbed />
    </>
  );
};

export default ComparisonsHub;
